/**
 * Performance Monitoring Utilities
 * 
 * Tracks loading times and performance metrics for async components
 * to help optimize the loading experience.
 */

interface LoadingMetric {
  componentId: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  error?: string;
}

class PerformanceMonitor {
  private metrics: LoadingMetric[] = [];
  private activeLoads = new Map<string, number>();

  /**
   * Start tracking a component load
   */
  startLoad(componentId: string): void {
    const startTime = performance.now();
    this.activeLoads.set(componentId, startTime);
  }

  /**
   * End tracking a component load
   */
  endLoad(componentId: string, success: boolean = true, error?: string): void {
    const startTime = this.activeLoads.get(componentId);
    if (!startTime) return;

    const endTime = performance.now();
    const duration = endTime - startTime;

    const metric: LoadingMetric = {
      componentId,
      startTime,
      endTime,
      duration,
      success,
      error
    };

    this.metrics.push(metric);
    this.activeLoads.delete(componentId);

    // Log slow loads in development
    if (!import.meta.env.PROD && duration > 1000) {
      console.warn(`Slow component load detected: ${componentId} took ${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Get performance metrics
   */
  getMetrics(): LoadingMetric[] {
    return [...this.metrics];
  }

  /**
   * Get average loading time for a component
   */
  getAverageLoadTime(componentId: string): number {
    const componentMetrics = this.metrics.filter(
      m => m.componentId === componentId && m.success && m.duration
    );

    if (componentMetrics.length === 0) return 0;

    const totalTime = componentMetrics.reduce((sum, m) => sum + (m.duration || 0), 0);
    return totalTime / componentMetrics.length;
  }

  /**
   * Get slowest loading components
   */
  getSlowestComponents(limit: number = 5): Array<{ componentId: string; avgTime: number }> {
    const componentTimes = new Map<string, number[]>();

    this.metrics.forEach(metric => {
      if (metric.success && metric.duration) {
        if (!componentTimes.has(metric.componentId)) {
          componentTimes.set(metric.componentId, []);
        }
        componentTimes.get(metric.componentId)!.push(metric.duration);
      }
    });

    const averages = Array.from(componentTimes.entries()).map(([componentId, times]) => ({
      componentId,
      avgTime: times.reduce((sum, time) => sum + time, 0) / times.length
    }));

    return averages
      .sort((a, b) => b.avgTime - a.avgTime)
      .slice(0, limit);
  }

  /**
   * Clear metrics (useful for testing)
   */
  clearMetrics(): void {
    this.metrics = [];
    this.activeLoads.clear();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Helper functions for easier usage
export const startComponentLoad = (componentId: string) => {
  performanceMonitor.startLoad(componentId);
};

export const endComponentLoad = (componentId: string, success: boolean = true, error?: string) => {
  performanceMonitor.endLoad(componentId, success, error);
};

export const getLoadingMetrics = () => {
  return performanceMonitor.getMetrics();
};

// Development helper to log performance summary
if (!import.meta.env.PROD) {
  (window as any).getPerformanceReport = () => {
    const metrics = performanceMonitor.getMetrics();
    const slowest = performanceMonitor.getSlowestComponents();
    
    console.group('Component Loading Performance Report');
    console.log('Total loads:', metrics.length);
    console.log('Failed loads:', metrics.filter(m => !m.success).length);
    console.log('Slowest components:', slowest);
    console.groupEnd();
    
    return { metrics, slowest };
  };
}